import { useState, type ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { useAuthStore } from '@/lib/auth-store';
import { AsrProviderSettingsCard } from '@/components/settings/asr-provider-settings';
import { AsrCredentialSettingsCard } from '@/components/settings/asr-credential-settings';
import { MaxBatchUploadSettingsCard } from '@/components/settings/max-batch-upload-settings';
import { AiTextAnalysisSettingsCard } from '@/components/settings/ai-text-analysis-settings';
import { AsrCorrectionSettingsCard } from '@/components/settings/asr-correction-settings';
import { DeepseekCredentialSettingsCard } from '@/components/settings/deepseek-credential-settings';
import { AiScoringRulesSettingsCard } from '@/components/settings/ai-scoring-rules-settings';
import { TrainingScoreSettingsCard } from '@/components/settings/training-score-settings';
import { KpiPointRulesSettingsCard } from '@/components/settings/kpi-point-rules-settings';
import { CourseCategoriesSettingsCard } from '@/components/settings/course-categories-settings';
import { QcMinDurationSettingsCard } from '@/components/settings/qc-min-duration-settings';
import { CompliantCallTargetSettingsCard } from '@/components/settings/compliant-call-target-settings';
import { LearningTargetMinutesSettingsCard } from '@/components/settings/learning-target-minutes-settings';
import { HrSettingsCard } from '@/components/settings/hr-settings';
import { SegmentedTabs } from '@/components/ui/segmented-tabs';
import { resolveActiveTab } from '@/lib/page-tabs';
import { AuditLogWorkspace } from '@/components/audit/audit-log-workspace';
import {
  channelLabel,
  displayVersion,
  formatReleaseTimeChinese,
  releaseMetadata,
  releaseSummaryLine,
  shortGitSha,
} from '@/lib/release-metadata';

/**
 * Issue #184 / #222 — 系统设置分页卡片化：一次只展示一个分区（Segmented Tab 切换），
 * 顺序与需求一致：
 *   1) 语音识别API：语音识别 Provider + 凭据（密钥不回显明文，仅状态 / 更新时间 / 操作者）
 *   2) 人力资源：部门管理 / 岗位管理 / 默认头像 / 默认密码
 *   3) KPI考核：课程分类 + 训练合格分规则
 *   4) 版本信息：当前版本与发布时间
 * 各子卡片均调用真实后端接口并运行时生效，绝不前端 state 模拟。
 */
const SETTINGS_TABS = [
  { key: 'asr', label: '语音识别API' },
  // Issue #227：AI文本分析分区位于「语音识别API」与「人力资源」之间。
  { key: 'aiText', label: 'AI文本分析' },
  { key: 'hr', label: '人力资源' },
  { key: 'kpi', label: 'KPI考核' },
  { key: 'version', label: '版本信息' },
  // Issue #224：系统设置内新增「登录日志」分区（与知识库模块共用同一审计工作区）。
  // Issue #226：登录日志唯一入口在系统设置（知识库内的重复入口已移除）。
  { key: 'logs', label: '登录日志' },
] as const;
type SettingsTab = (typeof SETTINGS_TABS)[number]['key'];

export function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const sha = shortGitSha();
  const canEditAdmin = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const [tab, setTab] = useState<SettingsTab>('asr');
  const activeTab = resolveActiveTab<SettingsTab>(
    tab,
    SETTINGS_TABS.map((t) => t.key),
  );
  return (
    <div className="flex flex-col gap-6">
      <header>
        <div className="text-xs text-slate-500">系统 / 设置</div>
        <h1 className="mt-1 text-2xl font-semibold text-slate-900">系统设置</h1>
      </header>

      <SegmentedTabs
        items={SETTINGS_TABS}
        value={activeTab}
        onChange={(k) => setTab(k)}
        data-testid="settings-tabs"
      />

      {activeTab === 'asr' ? (
        <Section title="语音识别API" subtitle="当前语音识别 Provider 与语音识别密钥配置（密钥不回显明文），以及质检批量上传上限。">
          <AsrProviderSettingsCard canEdit={canEditAdmin} />
          <AsrCredentialSettingsCard canEdit={canEditAdmin} />
          {/* Issue #385：质检中心批量上传最大文件数（GET/PATCH /system/max-batch-upload-config，
              移动端质检中心批量选择据此限制，运行时即时生效）。 */}
          <MaxBatchUploadSettingsCard canEdit={canEditAdmin} />
        </Section>
      ) : null}

      {activeTab === 'aiText' ? (
        <Section title="AI文本分析" subtitle="通话质检 AI 输出的建议话术 / 问题点字数上下限，以及 ASR 文本纠错（运行时即时生效）。">
          <AiTextAnalysisSettingsCard canEdit={canEditAdmin} />
          {/* Issue #291：ASR 文本纠错（GET/PATCH /system/asr-correction-config，
              SOP/质检 ASR 完成后、AI 评分前严格纠错；temperature 默认 0.1、最大 0.2）。 */}
          <AsrCorrectionSettingsCard canEdit={canEditAdmin} />
          {/* Issue #343：DeepSeek 多 Key 安全管理（新增/停用/标记默认/轮换/删除，加密落库，
              运行时即时生效，永不回显明文）。 */}
          <DeepseekCredentialSettingsCard canEdit={canEditAdmin} />
          {/* Issue #324：AI评分规则（AI文本纠错下方）。GET/PATCH /system/qc-scoring-rules-config，
              合格/良好/优秀下界，运行时即时生效，与后端 PASS/FAIL + 报告等级文案同一规则。 */}
          <AiScoringRulesSettingsCard canEdit={canEditAdmin} />
        </Section>
      ) : null}

      {activeTab === 'hr' ? (
        <Section title="人力资源" subtitle="部门管理、岗位管理、默认头像管理、默认密码。">
          <HrSettingsCard canEdit={canEditAdmin} />
        </Section>
      ) : null}

      {activeTab === 'kpi' ? (
        <Section title="KPI考核" subtitle="课程分类、训练合格分规则、积分规则、最短有效质检时长、目标分钟数与合规通话目标。">
          <CourseCategoriesSettingsCard canEdit={canEditAdmin} />
          <TrainingScoreSettingsCard canEdit={canEditAdmin} />
          {/* Issue #272：积分规则（GET/PATCH /system/kpi-point-rules，运行时即时生效——
              排行榜 / 首页 / 个人中心本月积分按新规则推导）。 */}
          <KpiPointRulesSettingsCard canEdit={canEditAdmin} />
          {/* Issue #232：最短有效质检时长（GET/PATCH /system/qc-min-duration-config，运行时即时生效）。 */}
          <QcMinDurationSettingsCard canEdit={canEditAdmin} />
          {/* Issue #263：合规通话目标（GET/PATCH /system/compliant-call-target-config，
              移动端首页「今天目标」即时生效）。 */}
          <CompliantCallTargetSettingsCard canEdit={canEditAdmin} />
          {/* Issue #278：学习目标分钟数（GET/PATCH /system/learning-target-minutes-config，
              移动端首页「目标 N 分钟」即时生效）。 */}
          <LearningTargetMinutesSettingsCard canEdit={canEditAdmin} />
        </Section>
      ) : null}

      {activeTab === 'version' ? (
        <Section title="版本信息" subtitle="当前版本与发布信息（统一发布元数据源）。">
          <Card title="版本信息">
          <p className="mb-4 text-sm font-medium text-slate-900" data-testid="admin-release-summary">
            {releaseSummaryLine()}
          </p>
          <dl className="grid grid-cols-2 gap-y-3 text-sm text-slate-700 md:grid-cols-3">
            <Item label="当前版本" value={displayVersion()} />
            <Item label="发布时间" value={formatReleaseTimeChinese(releaseMetadata.releaseTimeIso)} />
            <Item label="构建渠道" value={channelLabel()} />
            <Item label="构建版本号" value={sha || '—'} />
            <Item label="当前账号" value={user?.name ?? '—'} />
            <Item label="所属部门" value={user?.deptName ?? '—'} />
          </dl>
          <p className="mt-3 text-xs text-slate-500">
            版本信息来自统一发布元数据源（release/release-metadata.json），与移动端、Windows
            打包共用，避免各端版本号不一致。
          </p>
          </Card>
        </Section>
      ) : null}

      {activeTab === 'logs' ? (
        <Section title="登录日志" subtitle="系统成员登录 / 退出 / SOP 提交 / 质检录音 / AI 评分等关键操作审计。">
          <AuditLogWorkspace />
        </Section>
      ) : null}
    </div>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
        <p className="text-xs text-slate-500">{subtitle}</p>
      </div>
      {children}
    </section>
  );
}

function Item({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 font-medium text-slate-900">{value}</dd>
    </div>
  );
}
